"use client";

import { motion, useScroll, useTransform, AnimatePresence } from "motion/react";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import Image from "next/image";
import { ContactFormModal } from "./ContactFormModal";

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { scrollY } = useScroll();
  const backgroundColor = useTransform(
    scrollY,
    [0, 100],
    ["rgba(0, 0, 0, 0)", "#c19964cc"]
  );

  const navItems = ["ABOUT", "AMENITIES", "GALLERY", "FLOOR", "PAYMENT", "LOCATION", "CONTACT"];

  const handleNavClick = (item: string) => {
    // Map nav items to actual section IDs
    const sectionMap: { [key: string]: string } = {
      FLOOR: "floorplans",
      PAYMENT: "paymentplans",
    };

    const sectionId = sectionMap[item] || item.toLowerCase();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <>
      <motion.header
        style={{ backgroundColor }}
        className="fixed top-4 left-4 right-4 z-50 backdrop-blur-sm rounded-lg"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="text-white tracking-[0.3em] cursor-pointer hover:opacity-80 transition-opacity text-xl md:text-2xl"
            >
              <Image
                src="/images/logo/gate-eleven-logo.png"
                alt="Gate Eleven Residences Logo"
                width={200}
                height={40} 
                className="h-6 md:h-10 w-auto"
                priority
              />
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-4">
              {navItems.map((item, index) => (
                <motion.a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item);
                  }}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.1 }}
                  className="text-white/90 hover:text-white hover:bg-white/30 px-2 py-1 rounded-full transition-colors text-sm tracking-wider cursor-pointer"
                >
                  {item === "FLOOR"
                    ? "FLOOR PLANS"
                    : item === "PAYMENT"
                    ? "PAYMENT PLAN"
                    : item}
                </motion.a>
              ))}

              {/* Register Interest Button */}
              <motion.button
                initial={{ opacity: 0, y: -20 }}
                animate={{ 
                  opacity: 1, 
                  y: 0,
                  scale: [1, 1.08, 1],
                  boxShadow: [
                    "0 0 0 0 rgba(255, 255, 255, 0.7), 0 4px 15px rgba(255, 255, 255, 0.3)",
                    "0 0 0 12px rgba(255, 255, 255, 0), 0 8px 30px rgba(255, 255, 255, 0.5)",
                    "0 0 0 0 rgba(255, 255, 255, 0), 0 4px 15px rgba(255, 255, 255, 0.3)"
                  ]
                }}
                transition={{ 
                  duration: 0.5, 
                  delay: navItems.length * 0.1,
                  scale: {
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 0.5,
                    ease: "easeInOut"
                  },
                  boxShadow: {
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 0.5,
                    ease: "easeInOut"
                  }
                }}
                whileHover={{ 
                  scale: 1.1,
                  boxShadow: "0 0 30px rgba(255, 255, 255, 0.8), 0 0 50px rgba(193, 153, 100, 0.4)"
                }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowModal(true)}
                className="relative bg-gradient-to-r from-white to-white/95 text-black px-7 py-2.5 hover:from-white/95 hover:to-white transition-all text-sm tracking-[0.15em] font-semibold shadow-xl"
              >
                <span className="relative z-10">REGISTER INTEREST</span>
                <motion.div
                  className="absolute inset-0 bg-white opacity-0"
                  animate={{ opacity: [0, 0.5, 0] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    repeatDelay: 0.5
                  }}
                />
              </motion.button>
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden text-white z-50 relative"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation - Dropdown */}
        <AnimatePresence>
          {isOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden bg-[#c19964]/95 backdrop-blur-md rounded-b-lg overflow-hidden"
            >
              <div className="flex flex-col px-6 py-4">
                {navItems.map((item, index) => (
                  <motion.a
                    key={item}
                    href={`#${item.toLowerCase()}`}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(item);
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="text-white text-base tracking-[0.15em] hover:text-white/70 transition-colors py-3 border-b border-white/10 last:border-b-0"
                  >
                    {item === "FLOOR"
                      ? "FLOOR PLANS"
                      : item === "PAYMENT"
                      ? "PAYMENT PLAN"
                      : item}
                  </motion.a>
                ))}

                {/* Mobile Register Interest Button */}
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ 
                    opacity: 1, 
                    x: 0,
                    scale: [1, 1.05, 1],
                    boxShadow: [
                      "0 0 0 0 rgba(255, 255, 255, 0.7), 0 4px 15px rgba(255, 255, 255, 0.3)",
                      "0 0 0 10px rgba(255, 255, 255, 0), 0 8px 30px rgba(255, 255, 255, 0.5)",
                      "0 0 0 0 rgba(255, 255, 255, 0), 0 4px 15px rgba(255, 255, 255, 0.3)"
                    ]
                  }}
                  transition={{ 
                    duration: 0.3, 
                    delay: navItems.length * 0.05,
                    scale: {
                      duration: 1.5,
                      repeat: Infinity,
                      repeatDelay: 0.5,
                      ease: "easeInOut"
                    },
                    boxShadow: {
                      duration: 1.5,
                      repeat: Infinity,
                      repeatDelay: 0.5,
                      ease: "easeInOut"
                    }
                  }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowModal(true);
                    setIsOpen(false);
                  }}
                  className="relative bg-gradient-to-r from-white to-white/95 text-black px-7 py-3.5 hover:from-white/95 hover:to-white transition-all text-sm tracking-[0.15em] font-semibold mt-4 shadow-xl"
                >
                  <span className="relative z-10">REGISTER INTEREST</span>
                  <motion.div
                    className="absolute inset-0  bg-white opacity-0"
                    animate={{ opacity: [0, 0.5, 0] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      repeatDelay: 0.5
                    }}
                  />
                </motion.button>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>

        {/* Contact Form Modal */}
      </motion.header>
      <ContactFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </>
  );
}
